import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';
import GenericIcons from '../generics/GenericIcons';
import { SOURCE_IDS } from '../maps/mapConstants';
import '../../css/PublicMap.css';

// ──────────────────────────────────────────────
//  Constantes de visualización
// ──────────────────────────────────────────────
const ARTIFACT_MIN_ZOOM = 12;
const SOURCE_ID = 'nodal-capacity';

const MT_MIN_KW = 30;
const MT_MAX_KW = 9000;
const BT_MIN_KW = 1;
const BT_MAX_KW = 30;

const GRADIENT_STOPS = [
  'rgb(178, 24, 43)',
  'rgb(244, 109, 67)',
  'rgb(255, 200, 0)',
  'rgb(166, 217, 106)',
  'rgb(38, 220, 0)'
] as const;

// ──────────────────────────────────────────────
//  Tipos
// ──────────────────────────────────────────────
export type NodalCapacityFeature = {
  nodoId: string;
  nodoIdOpendss: string;
  nodoIdOrigen: string;
  alimentadorId: string;
  alimentadorNombre?: string;
  nivelTension: string;
  metodologiaVersion: string;
  generadoEn: string;
  x: number;
  y: number;
  latitud: number;
  longitud: number;
  capacidad: number;
};

export type LineasFeature = {
  tramoId: number;
  inicioLatitud: number;
  inicioLongitud: number;
  finLatitud: number;
  finLongitud: number;
};

export type NodalCapacityMapData = {
  capacidad: NodalCapacityFeature[];
  lineas: LineasFeature[];
};

type Props = {
  data: NodalCapacityMapData | null;
  loading: boolean;
  selectedComuna: string;
  mapCenter?: { lat: number; lng: number } | null;
};

// ──────────────────────────────────────────────
//  Sub-componentes de leyenda (extraídos de HeatMapPage)
// ──────────────────────────────────────────────
type LegendStop = { kw: number; label: string; color: string };

const LEGEND_MT: LegendStop[] = [
  { kw: MT_MIN_KW, label: `${MT_MIN_KW} kW`, color: GRADIENT_STOPS[0] },
  { kw: 2000, label: '2 000 kW', color: GRADIENT_STOPS[1] },
  { kw: 4500, label: '4 500 kW', color: GRADIENT_STOPS[2] },
  { kw: 7000, label: '7 000 kW', color: GRADIENT_STOPS[3] },
  { kw: MT_MAX_KW, label: `${MT_MAX_KW} kW`, color: GRADIENT_STOPS[4] }
];

const LEGEND_BT: LegendStop[] = [
  { kw: BT_MIN_KW, label: `${BT_MIN_KW} kW`, color: GRADIENT_STOPS[0] },
  { kw: 8, label: '8 kW', color: GRADIENT_STOPS[1] },
  { kw: 15, label: '15 kW', color: GRADIENT_STOPS[2] },
  { kw: 22, label: '22 kW', color: GRADIENT_STOPS[3] },
  { kw: BT_MAX_KW, label: `${BT_MAX_KW} kW`, color: GRADIENT_STOPS[4] }
];

function fmtKw(kw: number): string {
  if (kw >= 1000) {
    const miles = Math.floor(kw / 1000);
    const resto = String(kw % 1000).padStart(3, '0');
    return `${miles} ${resto}`;
  }
  return String(kw);
}

const GradientLegendBar: FunctionComponent<{
  title: string;
  subtitle: string;
  stops: LegendStop[];
  dotSize: number;
}> = ({ title, subtitle, stops, dotSize }) => {
  const gradient = stops.map((s) => s.color).join(', ');
  const mid = stops[Math.floor(stops.length / 2)];
  const first = stops[0];
  const last = stops[stops.length - 1];
  return (
    <div>
      <div className='vdr-map-legend-group-head'>
        <span
          aria-hidden
          className='vdr-map-legend-dot'
          style={{ width: dotSize, height: dotSize }}
        />
        <div className='vdr-map-legend-group-text'>
          <div className='vdr-map-legend-group-title'>{title}</div>
          <div className='vdr-map-legend-group-subtitle'>{subtitle}</div>
        </div>
      </div>

      <div className='vdr-map-legend-bar-wrap'>
        <div
          className='vdr-map-legend-bar'
          style={{ background: `linear-gradient(to right, ${gradient})` }}
        />
        <div aria-hidden className='vdr-map-legend-ticks'>
          {[0, 1, 2].map((i) => (
            <span key={i} />
          ))}
        </div>
      </div>

      <div className='vdr-map-legend-values'>
        <span>{fmtKw(first.kw)}</span>
        <span>{fmtKw(mid.kw)}</span>
        <span>{fmtKw(last.kw)}</span>
      </div>
    </div>
  );
};

function formatGeneradoEn(raw: string | null | undefined): string {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleString('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return raw;
  }
}

class ZoomDisplayControl {
  private container: HTMLDivElement;
  private onZoom?: () => void;

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'maplibregl-ctrl zoom-display';
    this.container.style.cssText =
      'padding:6px 9px;font-family:var(--vdr-font-mono);font-size:11px;line-height:1;' +
      'letter-spacing:0.04em;color:var(--vdr-ink);background:var(--vdr-white);' +
      'border:1px solid var(--vdr-line);border-radius:0;box-shadow:var(--vdr-shadow-raise);';
    this.container.textContent = '--';
  }

  onAdd(map: maplibregl.Map) {
    this.onZoom = () => (this.container.textContent = map.getZoom().toFixed(2));
    map.on('zoom', this.onZoom);
    this.onZoom();
    return this.container;
  }

  onRemove(map: maplibregl.Map) {
    if (this.onZoom) map.off('zoom', this.onZoom);
    this.container.remove();
  }
}

const CLICKABLE_LAYERS = ['nodal-mt', 'nodal-bt'] as const;

// ──────────────────────────────────────────────
//  Componente principal
// ──────────────────────────────────────────────
const NodalCapacityHeatMapView: FunctionComponent<Props> = ({
  data,
  loading,
  selectedComuna,
  mapCenter
}) => {
  const [showZoomDisclaimer, setShowZoomDisclaimer] = useState(false);
  const [clickedNode, setClickedNode] = useState<Partial<NodalCapacityFeature>>({});
  const [rightSidebarVisible, setRightSidebarVisible] = useState(true);
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const zoomHudAdded = useRef(false);
  const selectedFeatureId = useRef<number | null>(null);

  // Estabilizar con useMemo para que los effects no disparen en cada render
  const lineas = useMemo<Feature<Geometry, GeoJsonProperties>[]>(
    () =>
      (data?.lineas ?? []).map((row, idx) => ({
        type: 'Feature' as const,
        properties: { uniqueFeatureId: `line-${row.tramoId}-${idx}` },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [row.inicioLongitud, row.inicioLatitud],
            [row.finLongitud, row.finLatitud]
          ]
        }
      })),
    [data]
  );

  const capacidadData = useMemo(() => data?.capacidad ?? [], [data]);

  // Inicializar mapa
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '&copy; OpenStreetMap Contributors',
            maxzoom: 19
          }
        },
        // Mismo mapa base que components/maps/Map.tsx.
        layers: [
          {
            id: 'osm',
            type: 'raster',
            source: 'osm',
            paint: { 'raster-saturation': -0.95, 'raster-contrast': -0.2, 'raster-brightness-min': 0.15 }
          }
        ]
      },
      center: [-73.16717853, -40.57272065],
      zoom: 11.09
    });

    if (!zoomHudAdded.current) {
      map.current.addControl(new ZoomDisplayControl(), 'top-right');
      zoomHudAdded.current = true;
    }

    map.current.on('zoom', () => {
      if (!map.current) return;
      setShowZoomDisclaimer(map.current.getZoom() < ARTIFACT_MIN_ZOOM);
    });
  }, []);

  // Mover mapa al cambiar la comuna — zoom 13.5 para que los nodos sean visibles (opacity ≥ 0.85)
  useEffect(() => {
    if (!map.current || !mapCenter) return;
    map.current.easeTo({ center: [mapCenter.lng, mapCenter.lat], zoom: 13.5, duration: 600 });
  }, [mapCenter]);

  // Actualizar capa de líneas
  useEffect(() => {
    if (!map.current || lineas.length === 0) return;
    const m = map.current;
    const applyLines = () => {
      const fc: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: lineas };
      if (m.getSource(SOURCE_IDS.lines)) {
        (m.getSource(SOURCE_IDS.lines) as maplibregl.GeoJSONSource).setData(fc);
      } else {
        m.addSource(SOURCE_IDS.lines, { type: 'geojson', promoteId: 'uniqueFeatureId', data: fc });
      }
      if (!m.getLayer(SOURCE_IDS.lines)) {
        m.addLayer({
          id: SOURCE_IDS.lines,
          type: 'line',
          source: SOURCE_IDS.lines,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          minzoom: ARTIFACT_MIN_ZOOM,
          paint: { 'line-color': '#000', 'line-width': 2, 'line-opacity': 0.5 }
        });
      }
    };
    if (m.isStyleLoaded()) applyLines();
    else m.once('load', applyLines);
  }, [lineas]);

  // Actualizar capas de nodos
  useEffect(() => {
    console.log('[NodalHeatMap] capacidadData effect — items:', capacidadData.length, '| map ready:', !!map.current);
    if (!map.current || capacidadData.length === 0) return;
    const m = map.current;

    const geojson: GeoJSON.FeatureCollection<GeoJSON.Point, NodalCapacityFeature> = {
      type: 'FeatureCollection',
      features: capacidadData.map((d, i) => ({
        type: 'Feature',
        id: i,
        geometry: { type: 'Point', coordinates: [d.longitud, d.latitud] },
        properties: { ...d }
      }))
    };

    const addLayers = () => {
      console.log('[NodalHeatMap] addLayers — isStyleLoaded:', m.isStyleLoaded(), '| zoom:', m.getZoom().toFixed(2), '| source exists:', !!m.getSource(SOURCE_ID));
      if (m.getSource(SOURCE_ID)) {
        (m.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(geojson);
        console.log('[NodalHeatMap] source updated with', geojson.features.length, 'features');
      } else {
        m.addSource(SOURCE_ID, { type: 'geojson', data: geojson });
        console.log('[NodalHeatMap] source added with', geojson.features.length, 'features');
      }

      if (!m.getLayer('nodal-mt')) {
        m.addLayer({
          id: 'nodal-mt',
          type: 'circle',
          source: SOURCE_ID,
          minzoom: ARTIFACT_MIN_ZOOM,
          filter: ['==', ['get', 'nivelTension'], 'MT'],
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], ARTIFACT_MIN_ZOOM, 8, 14, 12, 17, 16],
            'circle-color': [
              'interpolate', ['linear'], ['get', 'capacidad'],
              MT_MIN_KW, GRADIENT_STOPS[0],
              2000,      GRADIENT_STOPS[1],
              4500,      GRADIENT_STOPS[2],
              7000,      GRADIENT_STOPS[3],
              MT_MAX_KW, GRADIENT_STOPS[4]
            ],
            'circle-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 13, 0.85],
            'circle-stroke-color': ['case', ['boolean', ['feature-state', 'selected'], false], '#000', 'rgba(80,80,80,0.5)'],
            'circle-stroke-width': ['case', ['boolean', ['feature-state', 'selected'], false], 4, 1],
            'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 13, 0.7]
          }
        });
      }

      if (!m.getLayer('nodal-bt')) {
        m.addLayer({
          id: 'nodal-bt',
          type: 'circle',
          source: SOURCE_ID,
          minzoom: ARTIFACT_MIN_ZOOM,
          filter: ['==', ['get', 'nivelTension'], 'BT'],
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], ARTIFACT_MIN_ZOOM, 4, 14, 7, 17, 10],
            'circle-color': [
              'interpolate', ['linear'], ['get', 'capacidad'],
              BT_MIN_KW, GRADIENT_STOPS[0],
              8,         GRADIENT_STOPS[1],
              15,        GRADIENT_STOPS[2],
              22,        GRADIENT_STOPS[3],
              BT_MAX_KW, GRADIENT_STOPS[4]
            ],
            'circle-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 13, 0.85],
            'circle-stroke-color': ['case', ['boolean', ['feature-state', 'selected'], false], '#000', 'rgba(80,80,80,0.3)'],
            'circle-stroke-width': ['case', ['boolean', ['feature-state', 'selected'], false], 3, 0.5],
            'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 13, 0.7]
          }
        });
      }

      console.log('[NodalHeatMap] layers nodal-mt exists:', !!m.getLayer('nodal-mt'), '| nodal-bt exists:', !!m.getLayer('nodal-bt'));

      const handleNodeClick = (
        e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }
      ) => {
        const f = e.features?.[0];
        if (!f) return;
        if (selectedFeatureId.current !== null) {
          m.setFeatureState({ source: SOURCE_ID, id: selectedFeatureId.current }, { selected: false });
        }
        m.setFeatureState({ source: SOURCE_ID, id: f.id as number }, { selected: true });
        selectedFeatureId.current = f.id as number;
        const props = f.properties as NodalCapacityFeature;
        setClickedNode(props);
        m.easeTo({ center: [props.longitud, props.latitud], zoom: 15.5, duration: 700, essential: true });
      };

      for (const layerId of CLICKABLE_LAYERS) {
        m.on('click', layerId, handleNodeClick);
      }

      m.on('click', (e) => {
        const feats = m.queryRenderedFeatures(e.point, { layers: [...CLICKABLE_LAYERS] });
        if (feats.length) return;
        if (selectedFeatureId.current !== null) {
          m.setFeatureState({ source: SOURCE_ID, id: selectedFeatureId.current }, { selected: false });
          selectedFeatureId.current = null;
        }
        setClickedNode({});
      });
    };

    const safeAddLayers = () => {
      try {
        addLayers();
      } catch (err) {
        console.error('[NodalCapacityHeatMapView] Error adding nodal layers:', err);
      }
    };

    // Llamar directamente: addSource/addLayer funcionan aunque _sourcesDirty sea true
    // (el único requisito es que style._loaded sea true, lo que ocurre desde el primer render).
    // No usar isStyleLoaded() porque el efecto de líneas anterior puede haber puesto
    // _sourcesDirty=true, haciendo que isStyleLoaded() devuelva false aunque el estilo
    // esté completamente inicializado.
    safeAddLayers();
  }, [capacidadData]);

  // Limpiar nodos cuando cambia la comuna (nueva búsqueda)
  useEffect(() => {
    if (!map.current) return;
    const m = map.current;
    setClickedNode({});
    selectedFeatureId.current = null;
    const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
    if (m.getSource(SOURCE_ID)) {
      (m.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(empty);
    }
    if (m.getSource(SOURCE_IDS.lines)) {
      (m.getSource(SOURCE_IDS.lines) as maplibregl.GeoJSONSource).setData(empty);
    }
  }, [selectedComuna]);

  return (
    <>
      {!rightSidebarVisible && (
        <div className='public-map-right-arrow-outside-container'>
          <button
            type='button'
            className='primary-button-style vdr-map-float-button'
            aria-label='Mostrar panel de información técnica'
            onClick={() => setRightSidebarVisible(true)}>
            <GenericIcons icon='arrowLeftMenu' className='vdr-map-float-button-icon' />
          </button>
        </div>
      )}

      <div className='public-map-content-container'>
        <div
          className='public-map-content-map-section'
          style={{ gridColumn: rightSidebarVisible ? '1/10' : '1/13' }}>
          <div ref={mapContainer} className='map'>
            {showZoomDisclaimer && (
              <div className='zoom-disclaimer'>DEBE ACERCARSE PARA VISUALIZAR EL MAPA DE CALOR</div>
            )}

            {loading && (
              <div className='public-map-content-map-loader-container'>
                <span className='loader'></span>
              </div>
            )}

            {!selectedComuna && !loading && (
              <div className='public-map-content-map-overlay-message'>
                Selecciona una región y comuna para ver los datos de capacidad nodal
              </div>
            )}

            {selectedComuna && !loading && data !== null && capacidadData.length === 0 && (
              <div className='public-map-content-map-overlay-message'>
                Sin resultados de capacidad nodal para esta comuna
              </div>
            )}

            <div className='map-legend vdr-map-legend'>
              <div className='vdr-map-legend-title'>Capacidad nodal</div>
              <GradientLegendBar title='Media tensión' subtitle='Nodos MT' stops={LEGEND_MT} dotSize={14} />
              <div aria-hidden className='vdr-map-legend-divider' />
              <GradientLegendBar title='Baja tensión' subtitle='Nodos BT' stops={LEGEND_BT} dotSize={8} />
              <div className='vdr-map-legend-foot'>Valores expresados en kW</div>
            </div>
          </div>
        </div>

        {rightSidebarVisible && (
          <div className='public-map-content-right-section'>
            <div className='public-map-arrow-inside-container'>
              <div className='public-map-arrow-inside-button-container'>
                <button
                  type='button'
                  className='vdr-map-panel-toggle'
                  aria-label='Ocultar panel de información técnica'
                  onClick={() => setRightSidebarVisible(false)}>
                  <GenericIcons icon='arrowLeftMenu' className='vdr-map-panel-toggle-icon' />
                </button>
                <p className='public-map-arrow-inside-text'>Información técnica</p>
              </div>
            </div>

            <table className='public-map-content-table-content'>
              <thead>
                <tr>
                  <th className='public-map-content-table-column public-map-content-table-header'>
                    Campo
                  </th>
                  <th className='public-map-content-table-column public-map-content-table-header'>
                    Contenido
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(clickedNode).length ? (
                  <>
                    <tr>
                      <td className='public-map-content-table-column'>Capacidad</td>
                      <td className='public-map-content-table-column'>
                        {clickedNode.capacidad != null
                          ? `${Number(clickedNode.capacidad).toFixed(2)} kW`
                          : '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className='public-map-content-table-column'>Nivel tensión</td>
                      <td className='public-map-content-table-column'>{clickedNode.nivelTension ?? '—'}</td>
                    </tr>
                    <tr>
                      <td className='public-map-content-table-column'>Alimentador</td>
                      <td className='public-map-content-table-column'>
                        {clickedNode.alimentadorNombre ?? clickedNode.alimentadorId ?? '—'}
                      </td>
                    </tr>
                    <tr>
                      <td className='public-map-content-table-column'>Nodo origen</td>
                      <td className='public-map-content-table-column'>{clickedNode.nodoIdOrigen ?? '—'}</td>
                    </tr>
                    <tr>
                      <td className='public-map-content-table-column'>Nodo OpenDSS</td>
                      <td className='public-map-content-table-column'>{clickedNode.nodoIdOpendss ?? '—'}</td>
                    </tr>
                    <tr>
                      <td className='public-map-content-table-column'>Metodología</td>
                      <td className='public-map-content-table-column'>{clickedNode.metodologiaVersion ?? '—'}</td>
                    </tr>
                    <tr>
                      <td className='public-map-content-table-column'>Generado</td>
                      <td className='public-map-content-table-column'>
                        {formatGeneradoEn(clickedNode.generadoEn)}
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={2} className='public-map-content-table-column-without-data'>
                      Sin datos
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default NodalCapacityHeatMapView;
